/**
 * Chrome on Android shows its autofill bar (key / card / pin icons) above the
 * keyboard on any text field that does not opt out, so typing a weight or a
 * chat message pushed that bar over the session screen. Kaiylo's own fields
 * (reps, weights, comments, search, chat…) never need browser autofill, so any
 * field that does not declare an `autocomplete` value gets `autocomplete="off"`.
 *
 * Left alone, so the password manager and address autofill keep working:
 *  - fields that already declare `autocomplete` themselves;
 *  - email / password / tel fields;
 *  - every field of a form that holds an email or password field (login,
 *    sign-up, reset password, invitation).
 *
 * Fields added later (modals, route changes) are handled by a
 * MutationObserver, and checked again on focus in case their form gained a
 * credential field after they mounted.
 *
 * `autocomplete="off"` alone does NOT hide the bar: Chrome Android decides from
 * the field's type (password_autofill_agent.cc → manual_filling_controller_
 * impl.cc). A plain text field shows it whenever the account has any saved
 * password, card or address; a textarea never does; a `type="search"` field
 * only when Chrome has suggestions for it — none with autocomplete off. So
 * the student's typing fields (sets, chat, video search) render with
 * APP_TEXT_INPUT_TYPE: `search` on Android, `text` elsewhere. It has to be the
 * React prop — React 19 writes `type` back on every render, so switching it in
 * the DOM from here would not stick. The search clear button is hidden in
 * src/index.css; give such fields an `enterKeyHint` so Enter keeps a sensible
 * label.
 */
const MARK = 'data-autofill-off';
const CREDENTIAL_TYPES = new Set(['email', 'password', 'tel']);
const FIELD_SELECTOR = 'input, textarea';

const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

// `type` for an app text field that must never get Chrome Android's bar.
export const APP_TEXT_INPUT_TYPE = isAndroid ? 'search' : 'text';
// Spread on such a field: the type, plus the marker src/index.css uses to
// hide the search clear button on these fields only (a real search box keeps
// it).
export const appTextInputProps = { type: APP_TEXT_INPUT_TYPE, 'data-app-text': '' };

const isCredentialForm = (field) => {
  const form = field.closest('form');
  return !!form && !!form.querySelector('input[type="email"], input[type="password"]');
};

const shouldOptOut = (field) => {
  if (field instanceof HTMLInputElement && CREDENTIAL_TYPES.has(field.type)) return false;
  return !isCredentialForm(field);
};

export const applyAutofillOff = (field) => {
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
  const managed = field.hasAttribute(MARK);
  // A field whose author chose a value keeps it.
  if (!managed && field.hasAttribute('autocomplete')) return;
  if (shouldOptOut(field)) {
    field.setAttribute('autocomplete', 'off');
    field.setAttribute(MARK, '');
  } else if (managed) {
    field.removeAttribute('autocomplete');
    field.removeAttribute(MARK);
  }
};

const applyWithin = (node) => {
  if (!(node instanceof Element)) return;
  if (node.matches(FIELD_SELECTOR)) applyAutofillOff(node);
  node.querySelectorAll(FIELD_SELECTOR).forEach(applyAutofillOff);
};

export default function installDisableAutofillBar(root = document) {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
  const target = root.body ?? root;
  applyWithin(target);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach(applyWithin));
  });
  observer.observe(target, { childList: true, subtree: true });

  const onFocusIn = (e) => applyAutofillOff(e.target);
  root.addEventListener('focusin', onFocusIn, true);

  return () => {
    observer.disconnect();
    root.removeEventListener('focusin', onFocusIn, true);
  };
}
